import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { CheckSquare, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { taskStatus, taskStatusLabel, taskStatusEmoji } from '../utils/taskStatus';

const API = process.env.REACT_APP_BACKEND_URL;

export default function TasksView() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const loadTasks = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/tasks`, { withCredentials: true });
      setTasks(data);
    } catch (error) {
      toast.error('Error al cargar las tareas');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  const toggleTaskComplete = async (taskId, currentStatus) => {
    try {
      await axios.patch(
        `${API}/api/tasks/${taskId}`,
        { completed: !currentStatus },
        { withCredentials: true }
      );
      toast.success(currentStatus ? 'Tarea reactivada' : 'Tarea completada');
      loadTasks();
    } catch (error) {
      toast.error('Error al actualizar la tarea');
    }
  };

  const deleteTask = async (taskId) => {
    if (!window.confirm('¿Estas seguro de eliminar esta tarea?')) return;

    try {
      await axios.delete(`${API}/api/tasks/${taskId}`, { withCredentials: true });
      toast.success('Tarea eliminada');
      loadTasks();
    } catch (error) {
      toast.error('Error al eliminar la tarea');
    }
  };

  const filteredTasks = tasks.filter((task) => {
    if (filter === 'completed') return task.completed;
    if (filter === 'pending') return !task.completed;
    return true;
  });

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="w-12 h-12 border-4 border-foreground border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="tasks-view">
      <div>
        <h1 className="text-4xl sm:text-5xl font-extrabold text-foreground mb-2" >
          Tareas
        </h1>
        <p className="text-muted-foreground">Gestiona tus tareas y responsabilidades</p>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 bg-surface rounded-2xl p-2 shadow-sm w-fit">
        <button
          data-testid="filter-all"
          onClick={() => setFilter('all')}
          className={`px-6 py-2 rounded-lg font-semibold transition-all ${
            filter === 'all'
              ? 'bg-[#1E3A5F] text-white shadow-md'
              : 'text-muted-foreground hover:bg-muted'
          }`}
        >
          Todas ({tasks.length})
        </button>
        <button
          data-testid="filter-pending"
          onClick={() => setFilter('pending')}
          className={`px-6 py-2 rounded-lg font-semibold transition-all ${
            filter === 'pending'
              ? 'bg-[#1E3A5F] text-white shadow-md'
              : 'text-muted-foreground hover:bg-muted'
          }`}
        >
          Pendientes ({tasks.filter((t) => !t.completed).length})
        </button>
        <button
          data-testid="filter-completed"
          onClick={() => setFilter('completed')}
          className={`px-6 py-2 rounded-lg font-semibold transition-all ${
            filter === 'completed'
              ? 'bg-[#1E3A5F] text-white shadow-md'
              : 'text-muted-foreground hover:bg-muted'
          }`}
        >
          Completadas ({tasks.filter((t) => t.completed).length})
        </button>
      </div>

      {/* Tasks List */}
      <div className="bg-surface rounded-2xl shadow-md p-8">
        {filteredTasks.length === 0 ? (
          <p className="text-muted-foreground text-center py-8">No hay tareas en esta categoría</p>
        ) : (
          <div className="space-y-3">
            {filteredTasks.map((task) => (
              <div
                key={task.id}
                data-testid={`task-${task.id}`}
                className={`flex items-center gap-4 p-4 rounded-2xl hover:shadow-md transition-all ${
                  task.completed ? 'bg-emerald-100 dark:bg-emerald-500/15' : 'bg-surface-2'
                }`}
              >
                <button
                  data-testid={`toggle-task-${task.id}`}
                  onClick={() => toggleTaskComplete(task.id, task.completed)}
                  className={`w-6 h-6 border-2 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${
                    task.completed
                      ? 'border-[#4CAF50] bg-[#4CAF50]'
                      : 'border-border hover:border-[#4CAF50]'
                  }`}
                >
                  {task.completed && <CheckSquare className="w-4 h-4 text-white" strokeWidth={2} />}
                </button>

                <div className="flex-1">
                  <h3 className={`font-semibold ${
                    task.completed ? 'line-through text-muted-foreground' : 'text-foreground'
                  }`}>
                    {task.title}
                  </h3>
                  {task.description && (
                    <p className="text-sm text-muted-foreground">{task.description}</p>
                  )}
                  {task.due_date && (
                    <p className="text-xs text-muted-foreground mt-1 flex items-center gap-2 flex-wrap">
                      <span>📅 {new Date(task.due_date + 'T00:00:00').toLocaleDateString('es-AR')}</span>
                      {task.due_time && (
                        <span data-testid={`task-time-${task.id}`}>🕚 {task.due_time}</span>
                      )}
                    </p>
                  )}
                  {(() => {
                    // Fuente de verdad ÚNICA — misma que Wingman/digest.
                    // Fase 2: la tarea vencida se muestra explícitamente
                    // en rojo — NO se auto-completa.
                    const st = taskStatus(task);
                    if (st === 'sin_fecha' || st === 'proxima') return null;
                    const badgeCls = {
                      vencida: 'bg-red-100 text-red-800 dark:bg-red-500/20 dark:text-red-300',
                      hoy: 'bg-orange-100 text-orange-800 dark:bg-orange-500/20 dark:text-orange-300',
                      completada: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-300',
                    }[st] || 'bg-slate-100 text-slate-700';
                    return (
                      <span
                        data-testid={`task-status-${task.id}`}
                        data-status={st}
                        className={`inline-flex items-center gap-1 mt-2 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${badgeCls}`}
                      >
                        <span aria-hidden="true">{taskStatusEmoji(st)}</span> {taskStatusLabel(st)}
                      </span>
                    );
                  })()}
                </div>

                {task.priority === 'high' && !task.completed && (
                  <span className="px-3 py-1 bg-red-100 text-red-700 text-xs font-semibold rounded-full">Alta</span>
                )}

                <button
                  data-testid={`delete-task-${task.id}`}
                  onClick={() => deleteTask(task.id)}
                  className="p-2 text-red-500 hover:bg-red-50 rounded-full transition-all"
                >
                  <Trash2 className="w-5 h-5" strokeWidth={1.5} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}